import React, { useState, useRef, useMemo, useCallback } from 'react';
import { createStelle } from './api';
import './Analytics.css';

import { KpiStrip, BarChart } from './analytics/AnalyticsCharts';
import AnalyticsTodo from './analytics/AnalyticsTodo';
import { ApplicationsTable } from './analytics/AnalyticsTable';
import AnalyticsCalendar from './analytics/AnalyticsCalendar';
import { DEMO_STELLEN, DEMO_TODOS } from './analytics/AnalyticsDemoData';

const RANGE_TABS = [
  { key: '7',   label: '7 T'  },
  { key: '30',  label: '30 T' },
  { key: '90',  label: '90 T' },
  { key: 'all', label: 'Alle' },
];

function filterByRange(stellen, range) {
  if (range === 'all') return stellen;
  const days = parseInt(range, 10);
  const cutoff = new Date(Date.now() - days * 86400000);
  return stellen.filter(s => !s.erstellt_am || new Date(s.erstellt_am) >= cutoff);
}

const WIDGET_LABELS = ['To-Do', 'Kalender'];

function WidgetStack({ children }) {
  const [index, setIndex] = useState(0);
  const startX = useRef(null);
  const arr = React.Children.toArray(children);
  const count = arr.length;

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx < -50 && index < count - 1) setIndex(i => i + 1);
    if (dx > 50 && index > 0) setIndex(i => i - 1);
    startX.current = null;
  };

  return (
    <div className="an-widget-stack an-card" id="tutorial-an-widget" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="an-widget-viewport">
        <div className="an-widget-track" style={{ transform: `translateX(${-index * 100}%)` }}>
          {arr.map((child, i) => (
            <div key={i} className="an-widget-slide">{child}</div>
          ))}
        </div>
      </div>
      <div className="an-widget-pills">
        {WIDGET_LABELS.map((label, i) => (
          <button
            key={i}
            className={`an-widget-pill${i === index ? ' an-widget-pill--active' : ''}`}
            onClick={() => setIndex(i)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Analytics({ stellen = [], setStellen, markierteIds, onMarkierenToggle }) {
  const [range, setRange] = useState('all');
  const [demoMode, setDemoMode] = useState(false);

  const activeStellen = demoMode ? DEMO_STELLEN : stellen;
  const filtered = useMemo(() => filterByRange(activeStellen, range), [activeStellen, range]);

  const handleLoeschen = useCallback((id) => {
    setStellen?.(prev => prev.filter(s => s.id !== id));
  }, [setStellen]);

  const handleStelleGeaendert = useCallback((id, changes) => {
    setStellen?.(prev => prev.map(s => s.id === id ? { ...s, ...changes } : s));
  }, [setStellen]);

  const handleExternErstellen = async (daten) => {
    try {
      const res = await createStelle({ ...daten, quelle: 'extern' });
      setStellen?.(prev => [...prev, res.data]);
    } catch (err) { console.error('Fehler beim Erstellen', err); }
  };

  return (
    <div className="analytics-view">

      {/* ── Page Header ── */}
      <header className="analytics-header">
        <div>
          <h1 className="analytics-title">Analytics</h1>
          <p className="analytics-subtitle">Dein Bewerbungs-Cockpit — alles auf einen Blick.</p>
        </div>
        <button
          className={`an-demo-toggle${demoMode ? ' an-demo-toggle--on' : ''}`}
          onClick={() => setDemoMode(v => !v)}
        >
          <span className="an-demo-dot" />
          {demoMode ? 'Demo läuft' : 'Vorschau'}
        </button>
      </header>

      {/* ── Demo Banner ── */}
      {demoMode && (
        <div className="an-demo-banner">
          Vorschau-Modus aktiv · Diese Daten sind simuliert
        </div>
      )}

      {/* ── KPI Strip ── */}
      <KpiStrip stellen={filtered} />

      {/* ── Bar Chart | Widget Stack ── */}
      <div className="an-grid an-grid--2-1">
        <div className="an-stack">
          <BarChart stellen={activeStellen} />
        </div>
        <WidgetStack>
          <AnalyticsTodo demoTodos={demoMode ? DEMO_TODOS : null} />
          <AnalyticsCalendar stellen={activeStellen} />
        </WidgetStack>
      </div>

      {/* ── Applications Table ── */}
      <ApplicationsTable
        stellen={filtered}
        markierteIds={markierteIds}
        onMarkierenToggle={onMarkierenToggle}
        onLoeschen={demoMode ? undefined : handleLoeschen}
        onStelleGeaendert={demoMode ? undefined : handleStelleGeaendert}
        onExternErstellen={demoMode ? undefined : handleExternErstellen}
      />

    </div>
  );
}
